/**
 * Data hooks of the admin price table.
 *
 * Reads: the price-view product list, the header summary and the lazily
 * loaded size prices of one product. Writes: every mutation that changes a
 * row fetches that row again with the table's current `ekPlatform` /
 * `platform` and swaps it into the cached pages instead of invalidating the
 * list, so the other rows (and any cell an admin is typing in) keep their
 * identity. Only the bulk action (without `dryRun`) invalidates the list.
 *
 * Hooks return errors to the caller; toasts and texts live in the
 * components under `components/admin/`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as adminApi from '@/api/admin';
import type {
  AdminPriceSummaryQuery,
  AdminProductPriceQuery,
  BulkChannelReleaseInput,
  BulkPriceInput,
  PriceRowParams,
} from '@/api/admin';
import { adminProductKeys, refreshPriceRow } from './useAdminProducts';

// ═══════════════════════════════════════════════════════════════════
// QUERIES
// ═══════════════════════════════════════════════════════════════════

/**
 * One page of price table rows. The previous page stays visible while the
 * next one loads, like the classic product list.
 */
export function useAdminProductPriceRows(query: AdminProductPriceQuery = {}) {
  return useQuery({
    queryKey: adminProductKeys.priceList(query),
    queryFn: () => adminApi.getAdminProductPriceRows(query),
    placeholderData: (previous) => previous,
  });
}

/** Header counts over all products matching the filters. */
export function useAdminPriceSummary(query: AdminPriceSummaryQuery = {}) {
  return useQuery({
    queryKey: adminProductKeys.priceSummary(query),
    queryFn: () => adminApi.getAdminPriceSummary(query),
    placeholderData: (previous) => previous,
  });
}

/** Size prices of one product, loaded once its row is expanded. */
export function useSizePrices(productId: string) {
  return useQuery({
    queryKey: adminProductKeys.sizePrices(productId),
    queryFn: () => adminApi.getSizePrices(productId),
  });
}

// ═══════════════════════════════════════════════════════════════════
// MUTATIONS
// ═══════════════════════════════════════════════════════════════════

/**
 * Set or clear the product-level purchase or recommended sales price.
 * The caller sends one field per save; the API accepts both.
 */
export function useSetProductPrices(priceRowParams: PriceRowParams) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      input,
    }: {
      productId: string;
      input: adminApi.SetProductPricesInput;
    }) => adminApi.setProductPrices(productId, input),
    onSuccess: (_result, { productId }) =>
      refreshPriceRow(queryClient, productId, priceRowParams),
  });
}

/**
 * Set or clear the purchase price of one size. Refreshes the size list
 * of the product and its row (the row's variant aggregate changes).
 */
export function useSetSizePrice(priceRowParams: PriceRowParams) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      sizeId,
      purchasePrice,
    }: {
      productId: string;
      sizeId: string;
      purchasePrice: number | null;
    }) => adminApi.setSizePrice(productId, sizeId, purchasePrice),
    onSuccess: async (_result, { productId }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: adminProductKeys.sizePrices(productId),
        }),
        refreshPriceRow(queryClient, productId, priceRowParams),
      ]);
    },
  });
}

/**
 * Shared tail of the method mutations: assignments feed the Creator and
 * Direct Sale flows and the product detail page, so those caches are
 * invalidated next to the row refresh.
 */
async function afterMethodChange(
  queryClient: ReturnType<typeof useQueryClient>,
  productId: string,
  priceRowParams: PriceRowParams
): Promise<void> {
  await refreshPriceRow(queryClient, productId, priceRowParams);
  queryClient.invalidateQueries({
    queryKey: adminProductKeys.detail(productId),
  });
  queryClient.invalidateQueries({ queryKey: ['products'] });
}

/** Offer a method on every active position of a product. */
export function useOfferProductMethod(priceRowParams: PriceRowParams) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      methodId,
    }: {
      productId: string;
      methodId: string;
    }) => adminApi.offerProductMethod(productId, methodId),
    onSuccess: (_result, { productId }) =>
      afterMethodChange(queryClient, productId, priceRowParams),
  });
}

/**
 * Withdraw a method from every active position of a product. The API
 * answers 409 when it is the last method on a position; the caller shows
 * `getProductMethodUsage` before asking for confirmation.
 */
export function useWithdrawProductMethod(priceRowParams: PriceRowParams) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      methodId,
    }: {
      productId: string;
      methodId: string;
    }) => adminApi.withdrawProductMethod(productId, methodId),
    onSuccess: (_result, { productId }) =>
      afterMethodChange(queryClient, productId, priceRowParams),
  });
}

/**
 * Bulk price change. A preview (`dryRun: true`) touches nothing, so the
 * cache stays; the real run invalidates every product list page and the
 * summary because many rows may have changed. It does so on failure too:
 * a client-side timeout or a dropped connection says nothing about whether
 * the transaction committed, so the table must not keep showing old values.
 */
export function useBulkPrice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: BulkPriceInput) => adminApi.bulkPrice(input),
    onSettled: async (_result, _error, input) => {
      if (input.dryRun) return;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: adminProductKeys.lists() }),
        queryClient.invalidateQueries({
          queryKey: adminProductKeys.priceSummaries(),
        }),
        queryClient.invalidateQueries({
          queryKey: adminProductKeys.details(),
        }),
      ]);
    },
  });
}

/**
 * Bulk release or withdrawal of one admin channel. Like the real bulk
 * price run it invalidates the lists and the summary on failure too: a
 * timeout says nothing about whether the transaction committed.
 */
export function useBulkChannelRelease() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: BulkChannelReleaseInput) =>
      adminApi.bulkChannelRelease(input),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: adminProductKeys.lists() }),
        queryClient.invalidateQueries({
          queryKey: adminProductKeys.priceSummaries(),
        }),
        queryClient.invalidateQueries({ queryKey: ['products'] }),
      ]);
    },
  });
}
